/**
 * Exact decimal handling for the pricing management screens (#3034, T13b1).
 *
 * Management amounts travel as decimal TEXT in cents at four decimals
 * (`"7050.0000"` is 70,50 €), exactly what staff-api's `part-pricing.schema.ts`
 * accepts. Nothing here goes through a float: text is parsed into a BigInt scaled
 * by the number of decimals, so `1.005 €` is `100.5000` cents and never `100`.
 * Nothing coerces an invalid or blank entry into `0` — that would be a free price.
 *
 * Mirrors the contract of `packages/shared/src/pricing/part-price-resolution.ts`
 * (`parsePartPricingDecimal`, `formatPartPricingCents`, `MAX_PART_PRICING_CENTS4`)
 * and of the percent bounds of the schema; portal code does not import `packages/`
 * (docs/frontend-package-boundaries.md), so the copy is pinned by decimal.test.ts.
 */

/** Largest amount `numeric(12,4)` holds, as cents scaled by 10^4. */
const MAX_CENTS4_SCALED = 999_999_999_999n;
const CENTS_DECIMALS = 4;
/** Euros carry two decimals more than cents. */
const EURO_DECIMALS = CENTS_DECIMALS + 2;
const PERCENT_DECIMALS = 4;
/** The schema reads a string of at most this many characters. */
const MAX_INPUT_LENGTH = 24;

export type DecimalFailure = 'required' | 'invalid' | 'precision' | 'range';
export type DecimalResult = { ok: true; value: string } | { ok: false; error: DecimalFailure };

const POWERS = new Map<number, bigint>();
function pow10(decimals: number): bigint {
  let power = POWERS.get(decimals);
  if (power === undefined) {
    power = 10n ** BigInt(decimals);
    POWERS.set(decimals, power);
  }
  return power;
}

const DECIMAL_PATTERN = /^([+-]?)(\d+)(?:[.,](\d+))?$/;

/** `text` as an integer scaled by 10^decimals, or the reason it cannot be read exactly. */
function parseScaled(
  text: string,
  decimals: number,
): { ok: true; value: bigint } | { ok: false; error: Exclude<DecimalFailure, 'range'> } {
  const trimmed = text.trim();
  if (trimmed === '') return { ok: false, error: 'required' };
  if (trimmed.length > MAX_INPUT_LENGTH) return { ok: false, error: 'invalid' };
  const match = DECIMAL_PATTERN.exec(trimmed);
  if (!match) return { ok: false, error: 'invalid' };
  const fraction = (match[3] ?? '').replace(/0+$/, '');
  if (fraction.length > decimals) return { ok: false, error: 'precision' };
  const digits = `${match[2] ?? ''}${fraction.padEnd(decimals, '0')}`;
  const magnitude = BigInt(digits);
  return { ok: true, value: match[1] === '-' ? -magnitude : magnitude };
}

function scaledToText(value: bigint, decimals: number): string {
  const negative = value < 0n;
  const magnitude = negative ? -value : value;
  const power = pow10(decimals);
  const whole = magnitude / power;
  const fraction = (magnitude % power).toString().padStart(decimals, '0');
  return `${negative ? '-' : ''}${whole.toString()}.${fraction}`;
}

/**
 * A fixed net price typed in euros → canonical cents text at four decimals.
 * Up to six decimals of a euro (four of a cent); non-negative; at most `99999999.9999` cents.
 */
export function eurosInputToCents4(input: string): DecimalResult {
  const parsed = parseScaled(input, EURO_DECIMALS);
  if (!parsed.ok) return parsed;
  if (parsed.value < 0n || parsed.value > MAX_CENTS4_SCALED) return { ok: false, error: 'range' };
  // euros scaled by 10^6 is cents scaled by 10^4.
  return { ok: true, value: scaledToText(parsed.value, CENTS_DECIMALS) };
}

/**
 * A percent typed by the user → canonical text at four decimals, inside the bound.
 * A discount allows `0..100`, a margin `0 <= m < 100` (a 100 % margin has no finite price).
 */
export function percentInput(input: string, kind: 'discount' | 'margin'): DecimalResult {
  const parsed = parseScaled(input, PERCENT_DECIMALS);
  if (!parsed.ok) return parsed;
  const hundred = 100n * pow10(PERCENT_DECIMALS);
  const inside = parsed.value >= 0n && (kind === 'discount' ? parsed.value <= hundred : parsed.value < hundred);
  if (!inside) return { ok: false, error: 'range' };
  return { ok: true, value: scaledToText(parsed.value, PERCENT_DECIMALS) };
}

/** Server decimal (text, or a JSON number the server rounded to four decimals) as canonical four-decimal text. */
export function normalizeCents4(value: string | number): string | null {
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || Math.abs(value) > 99_999_999.9999) return null;
    // Formatting only: the number came from a four-decimal numeric(12,4) value.
    return normalizeCents4(value.toFixed(CENTS_DECIMALS));
  }
  const parsed = parseScaled(value, CENTS_DECIMALS);
  return parsed.ok ? scaledToText(parsed.value, CENTS_DECIMALS) : null;
}

/** Cents text (four decimals) back to what a user types: euros, no trailing zeros past the cent. */
export function cents4ToEurosInput(cents4: string): string {
  const parsed = parseScaled(cents4, CENTS_DECIMALS);
  if (!parsed.ok) return '';
  return trimDecimals(scaledToText(parsed.value, EURO_DECIMALS), 2, '.');
}

function trimDecimals(text: string, minDecimals: number, separator: string): string {
  const [whole = '0', fraction = ''] = text.split('.');
  let kept = fraction.replace(/0+$/, '');
  if (kept.length < minDecimals) kept = kept.padEnd(minDecimals, '0');
  return kept === '' ? whole : `${whole}${separator}${kept}`;
}

const separatorFor = (language: string): string => (language.startsWith('en') ? '.' : ',');

/** `12,3456 €`-style net amount from cents text; two to six decimals. */
export function formatEuros(cents4: string | number | null | undefined, language: string): string {
  if (cents4 === null || cents4 === undefined) return '';
  const normalized = normalizeCents4(cents4);
  if (normalized === null) return '';
  const parsed = parseScaled(normalized, CENTS_DECIMALS);
  if (!parsed.ok) return '';
  const euros = scaledToText(parsed.value, EURO_DECIMALS);
  return `${trimDecimals(euros, 2, separatorFor(language))}\u00A0€`;
}

/** Percent text without trailing zeros: `20`, `12,5`. */
export function formatPercent(percent: string | number | null | undefined, language: string): string {
  if (percent === null || percent === undefined) return '';
  const text = typeof percent === 'number' ? percent.toFixed(PERCENT_DECIMALS) : percent;
  const parsed = parseScaled(text, PERCENT_DECIMALS);
  if (!parsed.ok) return '';
  return `${trimDecimals(scaledToText(parsed.value, PERCENT_DECIMALS), 0, separatorFor(language))}\u00A0%`;
}

/** Percent text for an input field (`.` separator, no trailing zeros). */
export function percentToInput(percent: string): string {
  const parsed = parseScaled(percent, PERCENT_DECIMALS);
  return parsed.ok ? trimDecimals(scaledToText(parsed.value, PERCENT_DECIMALS), 0, '.') : '';
}

/** Round-half-up division of non-negative integers. */
function divideRounded(numerator: bigint, denominator: bigint): bigint {
  return (numerator * 2n + denominator) / (denominator * 2n);
}

function signedPercentText(numerator: bigint, denominator: bigint, language: string): string | null {
  if (denominator <= 0n) return null;
  const negative = numerator < 0n;
  const scaled = divideRounded((negative ? -numerator : numerator) * 10_000n, denominator); // percent at 2 decimals
  const text = trimDecimals(scaledToText(negative && scaled !== 0n ? -scaled : scaled, 2), 0, separatorFor(language));
  return `${text}\u00A0%`;
}

/** The markup that a margin implies: `m / (100 - m)`. A margin is on the SELLING price; a markup is on the cost. */
export function impliedMarkupOfMargin(marginPercent: string, language: string): string | null {
  const margin = parseScaled(marginPercent, PERCENT_DECIMALS);
  if (!margin.ok) return null;
  const hundred = 100n * pow10(PERCENT_DECIMALS);
  if (margin.value < 0n || margin.value >= hundred) return null;
  return signedPercentText(margin.value, hundred - margin.value, language);
}

/** `(sell - cost) / sell`: the margin a pair of prices really has (never a markup). */
export function marginOfPrices(sellCents4: string, costCents4: string, language: string): string | null {
  const sell = parseScaled(sellCents4, CENTS_DECIMALS);
  const cost = parseScaled(costCents4, CENTS_DECIMALS);
  if (!sell.ok || !cost.ok) return null;
  return signedPercentText(sell.value - cost.value, sell.value, language);
}

/** `(sell - cost) / cost`. */
export function markupOfPrices(sellCents4: string, costCents4: string, language: string): string | null {
  const sell = parseScaled(sellCents4, CENTS_DECIMALS);
  const cost = parseScaled(costCents4, CENTS_DECIMALS);
  if (!sell.ok || !cost.ok) return null;
  return signedPercentText(sell.value - cost.value, cost.value, language);
}

/** A signed change in cents text as euros with an explicit sign (`+1,50 €`, `-0,25 €`, `0,00 €`). */
export function formatSignedEuros(cents4: string | null | undefined, language: string): string {
  if (cents4 === null || cents4 === undefined) return '';
  const normalized = normalizeCents4(cents4);
  if (normalized === null) return '';
  const base = formatEuros(normalized, language);
  const parsed = parseScaled(normalized, CENTS_DECIMALS);
  return parsed.ok && parsed.value > 0n ? `+${base}` : base;
}

// ---------------------------------------------------------------------------
// Calendar dates (campaign and customer-price windows)
// ---------------------------------------------------------------------------

const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A real calendar date `YYYY-MM-DD` (`2026-02-30` is rejected, not rolled over). */
export function isCalendarDate(value: string): boolean {
  const match = DATE_PATTERN.exec(value);
  if (!match) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCFullYear(year);
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

/** A calendar date for reading (`30.10.2026`); the date is shown as stored, in no time zone. */
export function formatDate(iso: string, language: string): string {
  if (!isCalendarDate(iso)) return iso;
  return new Intl.DateTimeFormat(language, { timeZone: 'UTC', day: 'numeric', month: 'numeric', year: 'numeric' }).format(
    new Date(`${iso}T00:00:00Z`),
  );
}

/** Calendar days of the inclusive window `[from, to]` (both valid dates). */
export function windowDays(from: string, to: string): number {
  const ms = Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`);
  return Math.round(ms / 86_400_000) + 1;
}

export const MAX_CAMPAIGN_DAYS = 366;
export const MAX_CAMPAIGN_ITEMS = 500;
export const MAX_RECALCULATION_PARTS = 200;

export type WindowFailure = 'invalidDate' | 'order' | 'tooLong';

/** The first problem of a window, or null. Open ends (`''`) are allowed only when `allowOpen`. */
export function checkWindow(
  from: string,
  to: string,
  options: { allowOpen: boolean; maxDays?: number },
): WindowFailure | 'required' | null {
  if (!options.allowOpen && (from === '' || to === '')) return 'required';
  if ((from !== '' && !isCalendarDate(from)) || (to !== '' && !isCalendarDate(to))) return 'invalidDate';
  if (from !== '' && to !== '') {
    if (from > to) return 'order';
    if (options.maxDays !== undefined && windowDays(from, to) > options.maxDays) return 'tooLong';
  }
  return null;
}
